import type {
  WhatsAppDeliveryBucket,
  WhatsAppDeliveryCounts,
  WhatsAppDeliveryExportKind,
} from "custom-types";
import { whatsappErrorMessage } from "./whatsapp";

/*
 * The delivery report's display rules (claude/design/whatsapp-messaging/07-delivery-report.md),
 * shared by SageMIN and the control panel. The API does the counting and builds the reason
 * labels; nothing here recomputes them.
 */

export const WHATSAPP_DELIVERY_BUCKET_LABELS: Readonly<
  Record<WhatsAppDeliveryBucket, string>
> = {
  delivered: "Delivered",
  read: "Read",
  awaiting: "Awaiting report",
  failed: "Failed",
  unconfirmed: "Unconfirmed",
  rejected: "Rejected by Sparrow",
  send_failed: "Not sent",
  skipped: "Skipped",
  in_progress: "In progress",
};

/** The buckets the message list can be filtered to, in the report's order. */
export const WHATSAPP_DELIVERY_BUCKET_FILTER: readonly WhatsAppDeliveryBucket[] =
  [
    "delivered",
    "read",
    "awaiting",
    "failed",
    "unconfirmed",
    "rejected",
    "send_failed",
    "skipped",
    "in_progress",
  ];

/** A rate the API sends as a percent with one decimal; "—" when it is null (no accepted messages). */
export function formatDeliveryRate(rate: number | null | undefined): string {
  return typeof rate === "number" ? `${rate.toFixed(1)}%` : "—";
}

/** Seconds as "4 s" or "2 min 5 s"; "—" when there is none. */
export function formatDeliverySeconds(
  seconds: number | null | undefined,
): string {
  if (typeof seconds !== "number") return "—";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
}

/** Messages that never reached Sparrow's acceptance: `rejected` + `send_failed` + `skipped`. */
export function deliveryNotSent(
  counts: Pick<WhatsAppDeliveryCounts, "rejected" | "send_failed" | "skipped">,
): number {
  return counts.rejected + counts.send_failed + counts.skipped;
}

const KATHMANDU_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;

/** `YYYY-MM-DD` in Asia/Kathmandu (the report's zone), `daysBack` days before `now`. */
export function kathmanduDate(daysBack = 0, now: number = Date.now()): string {
  return new Date(now + KATHMANDU_OFFSET_MS - daysBack * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/** The CSV's file name (§3.7): `whatsapp-delivery-<kind>-<company slug|all>-<from>-<to>.csv`. */
export function deliveryCsvFilename(
  kind: WhatsAppDeliveryExportKind,
  company: string | null | undefined,
  dateFrom: string,
  dateTo: string,
): string {
  const slug = company
    ? company
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "company"
    : "all";
  return `whatsapp-delivery-${kind}-${slug}-${dateFrom}-${dateTo}.csv`;
}

/** Query params without the empty ones (`""`, null, undefined). */
export function cleanDeliveryParams<T extends object>(
  params: T,
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  ) as Record<string, string | number>;
}

function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * The sentence for a refused CSV download. The file is requested as a blob, so a 400 (such as
 * "Narrow the range: at most 50,000 messages per export.") arrives as a blob of JSON; read it
 * and word it like any other refusal. Falls back to `fallback`.
 */
export async function deliveryExportError(
  error: unknown,
  fallback: string,
): Promise<string> {
  if (typeof Blob !== "undefined" && error instanceof Blob) {
    try {
      return whatsappErrorMessage(
        JSON.parse(await readBlobText(error)),
        fallback,
      );
    } catch {
      return fallback;
    }
  }
  return whatsappErrorMessage(error, fallback);
}

/**
 * The messages CSV button's text. The file follows the list's filters (outcome, number search,
 * purpose, hall, company), so the button says how many messages it holds and, when a filter
 * beyond the dates is set, that filters are applied. While the list is loading or in error there
 * is no count to trust, so the label stays generic (and still says when filters are applied).
 */
export function deliveryMessagesCsvLabel(
  total: number | null | undefined,
  filtered: boolean,
): string {
  const base =
    typeof total === "number"
      ? `Download ${total.toLocaleString("en-IN")} message${total === 1 ? "" : "s"} (CSV)`
      : "Download messages CSV";
  return filtered ? `${base} \u2014 filters applied` : base;
}
